import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Placement,
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
} from "@floating-ui/dom";
import Portal from "app/components/portal";
import styles from "./styles.module.scss";

interface CoachmarkProps {
  /** Render the Coachmark adjacent to the anchor, so the anchor mounts first. */
  anchorRef: React.RefObject<HTMLElement>;
  title: string;
  body: string;
  onDismiss: () => void;
  /**
   * Adds "Skip for now" beside "Got it" (product ask 2026-09-17, no Figma
   * frame): the coach declines the item from its page instead of the
   * checklist row. The caller owns what a skip does.
   */
  onSkip?: () => void;
  /**
   * Pressing the control the arrow points at dismisses the popup as "Got it"
   * does (product ask 2026-09-17). This widens that region when the anchor
   * is one control of a group the coach may use interchangeably (the Thu
   * pill in the weekday row). Defaults to the anchor.
   */
  dismissRef?: React.RefObject<HTMLElement>;
  placement?: Placement;
  /**
   * Points the arrow at the anchor's trailing end while the body still flows
   * in the reading direction. Pair it with a `-start` placement: `-end` would
   * grow the body back over the anchor.
   */
  arrowAtAnchorEnd?: boolean;
  /**
   * Points the arrow at the anchor's centre; the body still flows per the
   * placement's alignment (`-end` grows it back over/left of the anchor —
   * Figma 535:24378 under a 160px button).
   */
  arrowAtAnchorCenter?: boolean;
}

const ANCHOR_GAP = 12;
const ARROW_SIZE = 14;
// Fixed inset, not floating-ui's `arrow` middleware: anchors wider than the
// popup clamp an anchor-centered arrow onto the rounded corner.
const ARROW_INSET = 20;
const ARROW_CENTER_INSET = ARROW_INSET + ARROW_SIZE / 2;
// How far inside the anchor's trailing edge the arrow points (Figma 112:14961:
// a 360px-wide field with the popup's leading edge 40px inside its right edge).
const ANCHOR_END_ARROW_INSET = 11;

// No useId in this React version: a module counter backs the ids that tie the
// popup's text to the anchor it points at.
let instanceCount = 0;

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

/**
 * The controls a screen reader can land on for this anchor: the anchor itself
 * when it is one, else the focusable controls inside it. The anchor is often
 * the wrapper the popup is positioned against (a header row, a toggle row)
 * rather than the control it points at, and a description on a wrapper is
 * never announced. Falls back to the anchor when nothing inside is focusable.
 */
const describedControls = (anchor: HTMLElement): HTMLElement[] => {
  if (anchor.matches(FOCUSABLE)) {
    return [anchor];
  }
  const inside = Array.from(anchor.querySelectorAll<HTMLElement>(FOCUSABLE));
  return inside.length ? inside : [anchor];
};

const OPPOSITE_SIDE: { [side: string]: string } = {
  top: "bottom",
  right: "left",
  bottom: "top",
  left: "right",
};

const Coachmark = ({
  anchorRef,
  title,
  body,
  onDismiss,
  onSkip,
  dismissRef,
  placement = "bottom-start",
  arrowAtAnchorEnd = false,
  arrowAtAnchorCenter = false,
}: CoachmarkProps) => {
  const { t } = useTranslation();
  const popupRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(
    null,
  );
  const [finalPlacement, setFinalPlacement] = useState<string>(placement);
  const [id] = useState(() => `academy-coachmark-${(instanceCount += 1)}`);
  const titleId = `${id}-title`;
  const bodyId = `${id}-body`;

  // Ties the hint to the control it points at: a screen reader landing on
  // that control hears the hint as its description. The popup renders in a
  // portal, so it cannot sit beside the control in the DOM; the attribute is
  // set on the control directly and whatever description it already carried
  // is restored when the hint goes.
  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) {
      return undefined;
    }
    const restores = describedControls(anchor).map((control) => {
      const previous = control.getAttribute("aria-describedby");
      control.setAttribute(
        "aria-describedby",
        [previous, titleId, bodyId].filter(Boolean).join(" "),
      );
      return () => {
        if (previous === null) {
          control.removeAttribute("aria-describedby");
        } else {
          control.setAttribute("aria-describedby", previous);
        }
      };
    });
    return () => restores.forEach((restore) => restore());
  }, [anchorRef, titleId, bodyId]);

  // Using the control the arrow points at counts as taking the hint (product
  // ask 2026-09-17): picking a weekday, opening the modal the anchor points
  // at, clicking into the field. It dismisses exactly as "Got it" does; a
  // click anywhere else leaves the popup up. Listening on the document and
  // testing containment at click time, rather than on the control itself,
  // survives the control re-rendering to a new node. click, not mousedown:
  // dismissing re-renders the page (router state), and a page that rebuilds
  // its button on every render (Spaces' empty state) would swap the node out
  // between press and release, so the button's own click never fired. By the
  // time the click bubbles here the control's handler has run; if that
  // handler navigated away, dismiss reads the live entry and does nothing.
  //
  // The listener is attached once and reads the current props through a ref.
  // Re-attaching it whenever onDismiss changed identity (every SetupHint
  // render) lost clicks: React flushes pending effects as a click's dispatch
  // begins, so a page that re-renders between press and release had the
  // listener removed and re-added mid-dispatch, and the DOM skips both.
  const current = useRef({ anchorRef, dismissRef, onDismiss });
  current.current = { anchorRef, dismissRef, onDismiss };
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const props = current.current;
      const region = (props.dismissRef || props.anchorRef).current;
      if (region && region.contains(event.target as Node)) {
        props.onDismiss();
      }
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  useEffect(() => {
    const anchor = anchorRef.current;
    const popup = popupRef.current;
    if (!anchor || !popup) {
      return undefined;
    }

    const cleanup = autoUpdate(anchor, popup, () => {
      computePosition(anchor, popup, {
        placement,
        middleware: [
          offset(({ rects }) => {
            // Slides the popup along the anchor so the arrow lands where
            // asked. Positive crossAxis moves it in the reading direction
            // for the top/bottom placements this supports.
            let crossAxis = 0;
            if (arrowAtAnchorEnd) {
              crossAxis =
                rects.reference.width -
                ANCHOR_END_ARROW_INSET -
                ARROW_CENTER_INSET;
            } else if (arrowAtAnchorCenter) {
              // The arrow sits ARROW_CENTER_INSET from the aligned edge, so
              // move that edge from the anchor's edge to its centre.
              const toCenter = rects.reference.width / 2 - ARROW_CENTER_INSET;
              crossAxis = placement.endsWith("-end") ? -toCenter : toCenter;
            }
            return { mainAxis: ANCHOR_GAP, crossAxis };
          }),
          flip(),
          shift({ padding: 8 }),
        ],
      }).then(({ x, y, placement: resolvedPlacement }) => {
        setPosition({ left: x, top: y });
        setFinalPlacement(resolvedPlacement);
      });
    });

    return cleanup;
  }, [anchorRef, placement, arrowAtAnchorEnd, arrowAtAnchorCenter]);

  const side = finalPlacement.split("-")[0];
  const alignedToEnd = finalPlacement.endsWith("-end");
  const arrowStyle: React.CSSProperties = {
    [OPPOSITE_SIDE[side]]: -ARROW_SIZE / 2,
  };
  if (side === "top" || side === "bottom") {
    arrowStyle[alignedToEnd ? "right" : "left"] = ARROW_INSET;
  } else {
    arrowStyle[alignedToEnd ? "bottom" : "top"] = ARROW_INSET;
  }

  return (
    <Portal>
      {/* A non-modal dialog (no aria-modal): it carries a control, which rules
          out tooltip, and it neither takes focus nor blocks the page. */}
      <div
        ref={popupRef}
        className={styles.popup}
        role="dialog"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        style={{
          left: position ? position.left : 0,
          top: position ? position.top : 0,
          visibility: position ? "visible" : "hidden",
        }}
      >
        <div className={styles.arrow} style={arrowStyle} />
        <p id={titleId} className={styles.title}>
          {title}
        </p>
        <p id={bodyId} className={styles.body}>
          {body}
        </p>
        <div className={styles.actions}>
          <button type="button" className={styles.dismiss} onClick={onDismiss}>
            {t("Got it")}
          </button>
          {onSkip && (
            <button type="button" className={styles.skip} onClick={onSkip}>
              {t("Skip for now")}
            </button>
          )}
        </div>
      </div>
    </Portal>
  );
};

export default Coachmark;
